import { useEffect, useMemo, useState } from 'react';
import { DataTable, expandAction, useDataTable, useDataTableExpansion } from '@odo/shared-ui';
import { buildPricingColumns, toDisplayRows } from './pricingColumns';
import { PracticeEligibilityModal } from './PracticeEligibilityModal';
import { useMyPermissions } from '../../../hooks/useMyPermissions';
import type { PricedProduct } from '../../../services/practiceProductPricing';

// The Pricing tab: a practice's full product catalog with resolved pricing.
// No card header - the tab name says it all; the discounted counter and the
// eligibility editor sit in a slim action row above the bare table card.
// Fill-height and fixed-page-sized like the embedded Invoices tab, and - as a
// practice-detail tab - gets the expand-to-fullscreen toggle the same way.

// Same fixed page size as the embedded Invoices tab (FILL_HEIGHT_PAGE_SIZE).
const PAGE_SIZE = 15;

interface PracticePricingSectionProps {
    practiceId: string;
    practiceName: string;
    pricedProducts: PricedProduct[];
    isLoading: boolean;
}

export function PracticePricingSection({
    practiceId,
    practiceName,
    pricedProducts,
    isLoading
}: PracticePricingSectionProps) {
    // No sortable columns here (never were) - the unsorted `useDataTable`
    // overload, so `page`/`limit` are the only state this hook holds for us.
    const table = useDataTable({ defaultLimit: PAGE_SIZE, mode: 'paged' });
    const expansion = useDataTableExpansion();
    const { hasPermission } = useMyPermissions();
    const canEditProducts = hasPermission('products.update');

    // Row id, or `${productId}:${versionId}` for per-version rows.
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
    const [showEligibility, setShowEligibility] = useState(false);
    const [search, setSearch] = useState('');

    const { setPage } = table;
    useEffect(() => {
        setPage(1);
    }, [practiceId, search, setPage]);

    const filteredProducts = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return pricedProducts;
        return pricedProducts.filter(
            (p) =>
                p.name.toLowerCase().includes(query) ||
                (p.category ?? '').toLowerCase().includes(query) ||
                p.versions.some((v) => v.label.toLowerCase().includes(query))
        );
    }, [pricedProducts, search]);

    const displayRows = useMemo(() => toDisplayRows(filteredProducts), [filteredProducts]);
    const totalPages = Math.max(1, Math.ceil(displayRows.length / table.limit));
    const currentPage = Math.min(table.page, totalPages);
    const pageRows = displayRows.slice((currentPage - 1) * table.limit, currentPage * table.limit);

    const columns = useMemo(
        () => buildPricingColumns({ expandedRowId, practiceName }),
        [expandedRowId, practiceName]
    );

    return (
        <div className="h-full min-h-0 flex flex-col gap-2.5">
            <DataTable
                label="Pricing"
                columns={columns}
                rows={pageRows}
                getRowKey={(row) => row.rowId}
                rowAction={{
                    onActivate: (row) =>
                        setExpandedRowId((current) => (current === row.rowId ? null : row.rowId)),
                    mode: 'click'
                }}
                tableLayout="auto"
                mode="paged"
                pagination={{
                    page: currentPage,
                    totalPages,
                    total: displayRows.length,
                    onPage: (p) => table.setPage(Math.min(Math.max(1, p), totalPages))
                }}
                isLoading={isLoading}
                loadingRows={
                    <tr>
                        <td colSpan={6} className="px-3 py-8 text-center">
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                <span className="text-sm">Loading pricing…</span>
                            </div>
                        </td>
                    </tr>
                }
                emptyState={
                    pricedProducts.length === 0
                        ? 'No priced products found.'
                        : 'No products match the search.'
                }
                className="overflow-hidden"
                search={{
                    value: search,
                    onChange: setSearch,
                    placeholder: 'Search product, category, or version…'
                }}
                actions={[
                    ...(canEditProducts
                        ? [
                              {
                                  key: 'manage-discounts',
                                  label: 'Manage discounts',
                                  variant: 'primary' as const,
                                  onClick: () => setShowEligibility(true)
                              }
                          ]
                        : []),
                    expandAction(expansion, { label: 'Pricing' })
                ]}
                expansion={expansion}
            />

            {canEditProducts && showEligibility && (
                <PracticeEligibilityModal
                    practiceId={practiceId}
                    practiceName={practiceName}
                    onClose={() => setShowEligibility(false)}
                />
            )}
        </div>
    );
}
